'use client';
import React from "react";
import { motion } from "framer-motion";

// NOTE: Some build environments rewrite icon package imports to CDN URLs which can fail.
// To avoid that external dependency, this file defines small local SVG icon components
// instead of importing from `lucide-react`. This makes the component self-contained
// and avoids the "Failed to fetch ... lucide-react" build error.

// --- Local icon primitives (tiny, accessible SVGs) ---
function SvgIcon({ children, size = 20, className = "", title = undefined }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : "img"}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

const UserGroup = (props) => (
  <SvgIcon {...props}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </SvgIcon>
);

const ClipboardList = (props) => (
  <SvgIcon {...props}>
    <path d="M9 11h6M9 15h6" />
    <path d="M9 7h6" />
    <path d="M9 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
  </SvgIcon>
);

const Clock = (props) => (
  <SvgIcon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </SvgIcon>
);

const Calendar = (props) => (
  <SvgIcon {...props}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </SvgIcon>
);

const Wallet = (props) => (
  <SvgIcon {...props}>
    <rect x="2" y="7" width="20" height="12" rx="2" ry="2" />
    <path d="M16 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
  </SvgIcon>
);

const Users = (props) => (
  <SvgIcon {...props}>
    <path d="M17 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M7 21v-2a4 4 0 0 1 3-3.87" />
    <circle cx="12" cy="7" r="4" />
  </SvgIcon>
);

const Layers = (props) => (
  <SvgIcon {...props}>
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </SvgIcon>
);

const CheckCircle = (props) => (
  <SvgIcon {...props}>
    <path d="M9 12l2 2 4-4" />
    <circle cx="12" cy="12" r="9" />
  </SvgIcon>
);

const Play = ({ size = 16, className = "", title = "Play" }) => (
  <SvgIcon size={size} className={className} title={title}>
    <path d="M5 3v18l15-9L5 3z" />
  </SvgIcon>
);

// Single-file React + Tailwind landing page component
// Default export a React component. Ready to drop into a React / Next.js app.
// Requirements: TailwindCSS configured and framer-motion installed (no external icon package required).

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased">
      {/* Top navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-600 to-cyan-400 flex items-center justify-center text-white font-bold">SP</div>
            <div>
              <h1 className="text-lg font-semibold">Simplify Work. Amplify Growth.</h1>
              <p className="text-xs text-slate-500">SME Command Center</p>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-700">
            <a className="hover:text-slate-900" href="#features">Features</a>
            <a className="hover:text-slate-900" href="#why">Why Us</a>
            <a className="hover:text-slate-900" href="#testimonials">Reviews</a>
            <a className="hover:text-slate-900" href="#pricing">Pricing</a>
            <button className="ml-2 rounded-md px-4 py-2 bg-indigo-600 text-white text-sm hover:bg-indigo-700">Get Started</button>
          </nav>

          <div className="md:hidden">
            <button aria-label="Open menu" className="p-2 rounded-md bg-slate-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl sm:text-5xl font-extrabold leading-tight"
            >
              Simplify work. <span className="text-indigo-600">Amplify growth.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-6 text-lg text-slate-600 max-w-2xl"
            >
              A unified business management platform built for SMEs — manage workforce, projects, timesheets, leaves, CRM, and reimbursements from a single dashboard.
            </motion.p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#signup" className="inline-flex items-center gap-3 rounded-md bg-indigo-600 px-5 py-3 text-white font-semibold shadow hover:bg-indigo-700">
                <Play size={16} />
                Start Free Trial
              </a>

              <a href="#demo" className="inline-flex items-center gap-3 rounded-md border border-slate-200 px-5 py-3 text-slate-700 bg-white hover:bg-slate-50">
                Book a Demo
              </a>
            </div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-slate-100 p-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-indigo-600">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-slate-800">All-in-one</div>
                  <div className="text-xs">No more tool hopping</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-slate-100 p-2">
                  <Clock size={18} className="text-indigo-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-800">Realtime</div>
                  <div className="text-xs">Live dashboards</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-slate-100 p-2">
                  <Users size={18} className="text-indigo-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-800">SME-focused</div>
                  <div className="text-xs">Built for small teams</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-slate-100 p-2">
                  <Wallet size={18} className="text-indigo-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-800">Affordable</div>
                  <div className="text-xs">Flexible plans</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative rounded-2xl p-6 bg-white shadow-xl border border-slate-100"
            >
              {/* Mock dashboard card */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500">Company</div>
                  <div className="font-semibold">Acme Innovations</div>
                </div>
                <div className="text-xs text-slate-500">Monthly View</div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <div className="h-28 rounded-lg bg-gradient-to-r from-indigo-50 to-cyan-50 p-3 flex flex-col justify-between">
                    <div className="text-xs text-slate-500">Active Projects</div>
                    <div className="text-2xl font-bold">8</div>
                    <div className="text-xs text-slate-500">+12% this month</div>
                  </div>
                </div>

                <div>
                  <div className="h-28 rounded-lg bg-slate-100 p-3 flex flex-col justify-between">
                    <div className="text-xs text-slate-500">Open Tickets</div>
                    <div className="text-2xl font-bold text-amber-600">3</div>
                    <div className="text-xs text-slate-500">Support</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t pt-4 text-sm text-slate-500">
                <div className="flex items-center justify-between">
                  <div>Timesheets pending</div>
                  <div className="font-semibold">5</div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="max-w-7xl mx-auto px-6 py-12">
          <h3 className="text-2xl font-semibold">Everything You Need to Manage Your Business</h3>
          <p className="mt-2 text-slate-600 max-w-2xl">From HR to projects to finance — one platform to rule them all.</p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard Icon={UserGroup} title="Workforce Management" desc="Streamline onboarding, roles, and employee records." />
            <FeatureCard Icon={Layers} title="Project & Task Management" desc="Plan, assign, and track progress using boards and timelines." />
            <FeatureCard Icon={Clock} title="Timesheet & Attendance" desc="Effortless time logging and approval workflows." />
            <FeatureCard Icon={Calendar} title="Leave & Reimbursement" desc="Submit and approve leaves and claims digitally." />
            <FeatureCard Icon={ClipboardList} title="CRM & Sales" desc="Manage leads, customers, and pipelines in one place." />
            <FeatureCard Icon={Wallet} title="Reports & Billing" desc="Clear insights for decisions, and easy billing exports." />
          </div>
        </section>

        {/* Why choose us */}
        <section id="why" className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-semibold">Designed for Small & Medium Businesses That Think Big</h3>
              <p className="mt-3 text-slate-600">We bundle the tools you actually need — without the complexity and cost of enterprise systems. Launch quickly, scale confidently.</p>

              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-1 text-indigo-600" />
                  <div>
                    <div className="font-medium">Unified dashboard</div>
                    <div className="text-sm text-slate-500">See HR, projects, and finance at a glance.</div>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-1 text-indigo-600" />
                  <div>
                    <div className="font-medium">Secure & compliant</div>
                    <div className="text-sm text-slate-500">Role-based access and data security.</div>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-1 text-indigo-600" />
                  <div>
                    <div className="font-medium">Custom workflows</div>
                    <div className="text-sm text-slate-500">Adapt approvals and processes to your needs.</div>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle className="mt-1 text-indigo-600" />
                  <div>
                    <div className="font-medium">Affordable plans</div>
                    <div className="text-sm text-slate-500">Flexible pricing for growing teams.</div>
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <div className="rounded-2xl bg-gradient-to-r from-indigo-50 to-cyan-50 p-8 shadow">
                <h4 className="font-semibold">Start with what you need</h4>
                <p className="mt-2 text-sm text-slate-600">Choose modules — pay for what you use. Add seats and features as you grow.</p>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <Stat label="Avg. time saved" value="12hrs/week" />
                  <Stat label="Teams onboarded" value="+200" />
                  <Stat label="Average NPS" value="+68" />
                  <Stat label="Integrations" value="10+" />
                </div>

                <div className="mt-6">
                  <a href="#signup" className="inline-flex items-center gap-3 rounded-md bg-white px-4 py-2 shadow text-sm font-medium">Start Free Trial</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="max-w-7xl mx-auto px-6 py-12">
          <h3 className="text-2xl font-semibold">What our customers say</h3>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Testimonial quote="Our entire operations — projects, HR, and client follow-ups — are now in one place. It’s like having 5 tools in one." name="Anita M., Director, Innovate Consulting" />

            <Testimonial quote="Timesheet approvals used to take days. Now it's done in hours. So much smoother." name="Ravi P., Operations Manager" />

            <Testimonial quote="Easy onboarding, great support, and the reports are actually useful for decision making." name="Shruti K., CFO" />
          </div>
        </section>

        {/* CTA */}
        <section id="pricing" className="max-w-7xl mx-auto px-6 py-12">
          <div className="rounded-2xl bg-indigo-600 p-8 text-white shadow-lg">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-semibold">Ready to simplify your business?</h3>
                <p className="mt-2 text-indigo-100 max-w-xl">Start a free trial or book a demo — get your team onboarded in days, not months.</p>
              </div>

              <div className="flex gap-3">
                <a href="#signup" className="rounded-md bg-white text-indigo-600 px-4 py-2 font-semibold">Start Free</a>
                <a href="#demo" className="rounded-md border border-white/30 px-4 py-2">Book a Demo</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-sm text-slate-600">© {new Date().getFullYear()} SME Portal. All rights reserved.</div>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <a href="#" className="hover:text-slate-900">Privacy</a>
            <a href="#" className="hover:text-slate-900">Terms</a>
            <a href="#" className="hover:text-slate-900">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Small reusable components below
function FeatureCard({ Icon, title, desc }) {
  return (
    <motion.div
      whileHover={{ translateY: -6 }}
      className="rounded-xl bg-white p-6 shadow-sm border border-slate-100"
    >
      <div className="flex items-center gap-4">
        <div className="rounded-md bg-indigo-50 p-3">
          <Icon className="text-indigo-600" />
        </div>
        <div>
          <h4 className="font-semibold">{title}</h4>
          <p className="mt-1 text-sm text-slate-500">{desc}</p>
        </div>
      </div>
    </motion.div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white p-4 shadow-sm border border-slate-100">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-semibold mt-1">{value}</div>
    </div>
  );
}

function Testimonial({ quote, name }) {
  return (
    <motion.blockquote whileHover={{ scale: 1.02 }} className="rounded-xl bg-white p-6 shadow-sm border border-slate-100">
      <p className="text-slate-700">“{quote}”</p>
      <footer className="mt-4 text-sm text-slate-500">— {name}</footer>
    </motion.blockquote>
  );
}
